import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Helper function to decode JWT token and extract expiry
const getTokenExpiry = (token: string): number | null => {
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch (error) {
    console.warn('[AuthStore] Failed to decode token:', error);
    return null;
  }
};

interface User {
  id: string;
  name: string;
  email: string;
  type: string;
  priceLevel: string;
  traderCode?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tokenExpiry: number | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  guestId: string | null;
  
  // Actions
  login: (authData: { token: string; refreshToken: string; user: User }) => Promise<void>;
  loginAsGuest: (guestData: { guestId: string; sessionToken: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  updateTokens: (tokens: { token: string; refreshToken: string }) => Promise<void>;
  isTokenExpired: () => boolean;
  validateAndRefreshToken: () => Promise<boolean>;
  clearExpiredAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      tokenExpiry: null,
      isAuthenticated: false,
      isGuest: false,
      guestId: null,

      login: (authData) => {
        // Decode JWT to get expiry time
        const tokenExpiry = getTokenExpiry(authData.token);
        set({
          user: authData.user,
          token: authData.token,
          refreshToken: authData.refreshToken,
          tokenExpiry,
          isAuthenticated: true,
          isGuest: false,
          guestId: null,
        });
      },

      loginAsGuest: (guestData) => {
        const tokenExpiry = getTokenExpiry(guestData.sessionToken);
        set({
          user: null,
          token: guestData.sessionToken,
          refreshToken: null,
          tokenExpiry,
          isAuthenticated: true,
          isGuest: true,
          guestId: guestData.guestId,
        });
      },

      logout: () =>
        set({
          user: null,
          token: null,
          refreshToken: null,
          tokenExpiry: null,
          isAuthenticated: false,
          isGuest: false,
          guestId: null,
        }),

      updateUser: (user) =>
        set((state) => ({
          ...state,
          user,
        })),

      updateTokens: (tokens) => {
        const tokenExpiry = getTokenExpiry(tokens.token);
        set((state) => ({
          ...state,
          token: tokens.token,
          refreshToken: tokens.refreshToken,
          tokenExpiry,
        }));
      },

      isTokenExpired: () => {
        const state = get();
        if (!state.tokenExpiry || !state.token) {
          return true;
        }
        // Add 2 minute buffer before actual expiry (reduced to prevent excessive refreshes)
        const bufferTime = 2 * 60 * 1000; // 2 minutes in milliseconds
        return Date.now() >= (state.tokenExpiry - bufferTime);
      },

      validateAndRefreshToken: async () => {
        const state = get();
        
        if (!state.isAuthenticated) {
          return false;
        }

        // Rate limit token refresh calls to prevent spam
        const { rateLimiter } = await import('../utils/rateLimiter');
        if (!rateLimiter.canMakeCall('token-refresh', 3, 60000)) {
          console.log('[AuthStore] Token refresh rate limited, skipping');
          return true; // Return true to prevent logout cascade
        }

        // If token is expired or about to expire
        if (state.isTokenExpired()) {
          console.log('[AuthStore] Token expired or expiring soon, attempting refresh...');
          
          if (!state.refreshToken && !state.isGuest) {
            console.log('[AuthStore] No refresh token available, logging out');
            state.logout();
            return false;
          }

          // For guest users, just logout if token expired
          if (state.isGuest) {
            console.log('[AuthStore] Guest token expired, logging out');
            state.logout();
            return false;
          }

          try {
            const response = await fetch('/api/v1/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken: state.refreshToken })
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                console.log('[AuthStore] Token refresh successful');
                state.updateTokens({
                  token: data.data.token,
                  refreshToken: data.data.refreshToken
                });
                return true;
              }
            }
            
            throw new Error('Refresh failed');
          } catch (error) {
            console.error('[AuthStore] Token refresh failed:', error);
            state.logout();
            return false;
          }
        }

        return true;
      },

      clearExpiredAuth: () => {
        const state = get();
        if (state.isTokenExpired()) {
          console.log('[AuthStore] Clearing expired authentication');
          state.logout();
        }
      },
    }),
    {
      name: 'dasoonegg-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiry: state.tokenExpiry,
        isAuthenticated: state.isAuthenticated,
        isGuest: state.isGuest,
        guestId: state.guestId,
      }),
      // Add migration and hydration logic
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          // Check if token is expired on app startup
          const isExpired = state.isTokenExpired();
          if (isExpired) {
            console.log('[AuthStore] Detected expired token on startup, clearing auth');
            // Directly clear the state instead of calling a method
            // Methods aren't properly available during rehydration
            state.token = null;
            state.refreshToken = null;
            state.tokenExpiry = null;
            state.user = null;
            state.isAuthenticated = false;
            state.isGuest = false;
            state.guestId = null;
          }
        }
      },
    }
  )
);